import { InputField, TextAreaField } from 'lib/iterable-hook-form';
import { PLACEMENT_DISPLAY_CONTENT } from 'modules/placements/constants';
import { addHelpIconTooltip } from 'modules/placements/utils';
import { useFormContext, useFormState } from 'react-hook-form';
import { Link } from 'ui/links';
import { ErrorText, FormField } from 'ui/formField';
import { H4 } from 'ui/theme';
import { getCharacterCountText } from 'modules/templateEditor/EditEmbedded/utils';
import { PlacementType, PlacementsCreateFormState } from '../../types';
import { CampaignLimitCounter, MediaLayoutDropdown } from './components';
import { PlacementTypeRadio } from './components/PlacementTypeRadio';
import { ContentBlock, HideableFormField, StyledSectionTitle } from './styles';

export const CONTENT = {
  campaignLimitHelpText: 'Max number of campaigns this placement can display at once.',
  campaignLimitFieldLabel: 'Campaign limit',
  descriptionFieldLabel: 'Description',
  learnMoreHelpText: 'Learn more',
  learnMoreLinkPath: PLACEMENT_DISPLAY_CONTENT.placementsDocumentationPath,
  mediaLayoutFieldLabel: 'Message Type',
  mediaLayoutFieldLabelTooltipContent: `These options correspond to out-of-the-box embedded message types provided by Iterable's SDKs. Choose a type that resembles how this placement will display its messages..`,
  nameFieldLabel: 'Name',
  nameFieldPlaceholder: 'Example: Homepage carousel',
  nameFieldError: 'Placement name is required',
  placementBlockHeader: 'Placement',
  previewLabel: 'Preview',
  previewTooltipContent:
    'These settings configure the embedded message preview that shows up in various places in Iterable.',
  typeFieldLabel: 'Layout',
};

const getCampaignLimitHelpText = () => (
  <>
    <span>{CONTENT.campaignLimitHelpText}</span>
    <Link
      hasOpenNewTabIcon
      size="caption"
      href={CONTENT.learnMoreLinkPath}
      rel="noopener noreferrer"
      target="_blank"
    >
      <span>{CONTENT.learnMoreHelpText}</span>
    </Link>
  </>
);

export const PlacementContentBlock = () => {
  const { errors } = useFormState<PlacementsCreateFormState>();
  const errorText = errors.placementName?.message || '';

  const { watch } = useFormContext();

  const placementType = watch('placementType');
  const description = watch('description');
  const isBannerType = placementType === PlacementType.Banner;

  return (
    <ContentBlock data-test="placement-content">
      <H4>{CONTENT.placementBlockHeader}</H4>
      <FormField labelText={CONTENT.nameFieldLabel} isHorizontalLayout>
        <InputField
          data-test="placement-name-input"
          name="placementName"
          placeholder={CONTENT.nameFieldPlaceholder}
        />
        {errorText && <ErrorText>{CONTENT.nameFieldError}</ErrorText>}
      </FormField>
      <FormField
        labelText={CONTENT.descriptionFieldLabel}
        restrictionText={getCharacterCountText(description ?? '')}
        isHorizontalLayout
      >
        <TextAreaField
          data-test="placement-description-textarea"
          maxLength={140}
          name="description"
          resize="vertical"
        />
      </FormField>

      <StyledSectionTitle>
        {addHelpIconTooltip(CONTENT.previewLabel, CONTENT.previewTooltipContent)}
      </StyledSectionTitle>

      <FormField
        labelText={addHelpIconTooltip(
          CONTENT.mediaLayoutFieldLabel,
          CONTENT.mediaLayoutFieldLabelTooltipContent,
        )}
        isHorizontalLayout
      >
        <MediaLayoutDropdown />
      </FormField>
      <FormField labelText={CONTENT.typeFieldLabel} isHorizontalLayout>
        <PlacementTypeRadio />
      </FormField>
      <HideableFormField
        $hidden={isBannerType}
        helpText={getCampaignLimitHelpText()}
        labelText={CONTENT.campaignLimitFieldLabel}
        isHorizontalLayout
      >
        <CampaignLimitCounter />
      </HideableFormField>
    </ContentBlock>
  );
};
